import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Dialog,
  Typography,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  AppBar,
  Toolbar,
  Paper,
  Link,
  Button
} from '@mui/material';
import { CloseOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

// Expandable text component
function ExpandableText({ text, maxLength = 50 }) {
  const [expanded, setExpanded] = useState(false);

  if (!text || text === '-') {
    return (
      <Typography variant="body2" color="text.secondary">
        -
      </Typography>
    );
  }

  const shouldTruncate = text.length > maxLength;

  if (!shouldTruncate) {
    return (
      <Typography variant="body2" color="text.secondary">
        {text}
      </Typography>
    );
  }

  return (
    <Box sx={{ maxWidth: 200 }}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          wordBreak: 'break-word',
          whiteSpace: expanded ? 'normal' : 'nowrap',
          overflow: expanded ? 'visible' : 'hidden',
          textOverflow: expanded ? 'clip' : 'ellipsis'
        }}
      >
        {expanded ? text : `${text.substring(0, maxLength)}...`}
      </Typography>
      <Link
        component="button"
        variant="caption"
        onClick={() => setExpanded(!expanded)}
        sx={{ mt: 0.5, cursor: 'pointer' }}
      >
        {expanded ? 'Show less' : 'Show more'}
      </Link>
    </Box>
  );
}

ExpandableText.propTypes = {
  text: PropTypes.string,
  maxLength: PropTypes.number
};

const getStatusColor = (status) => {
  const statusColors = {
    SCHEDULED: 'primary',
    COMPLETED: 'success',
    CANCELLED: 'error',
    PENDING: 'warning',
    IN_PROGRESS: 'info'
  };
  return statusColors[status] || 'default';
};

function AppointmentDetailsModal({ open, onClose, data }) {
  if (!data) return null;

  const appointments = Array.isArray(data) ? data : [data];
  const user = appointments[0]?.user;

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <AppBar sx={{ position: 'relative', bgcolor: 'primary.main' }}>
        <Toolbar>
          <Typography sx={{ flex: 1 }} variant="h5" component="div">
            Appointment History - {user?.first_name} {user?.last_name}
          </Typography>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
            <CloseOutlined />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        {/* User Info Summary */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Patient Information</Typography>
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Name</Typography>
              <Typography variant="body1">{user?.first_name} {user?.last_name}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Email</Typography>
              <Typography variant="body1">{user?.email || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Mobile</Typography>
              <Typography variant="body1">{user?.mobile || 'N/A'}</Typography>
            </Box>

          </Box>
        </Paper>

        {/* Appointments Table */}
        <Typography variant="h6" sx={{ mb: 2 }}>Appointment History ({appointments?.length})</Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700 }}>SL No.</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date & Time</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Doctor</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Specialization</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Notes</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Prescription</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Prescription File</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Created At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {appointments.map((appointment, index) => (
                <TableRow
                  key={appointment.id}
                  sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}
                >
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {dayjs(appointment.date_time).format('MMM DD, YYYY')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {dayjs(appointment.date_time).format('hh:mm A')}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {appointment.doctor?.name || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {appointment.doctor?.phone || ''}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={appointment.doctor?.specialization_name || 'N/A'}
                      size="small"
                      sx={{
                        bgcolor: '#e3f2fd',
                        color: '#1976d2',
                        fontWeight: 600,
                        fontSize: '0.75rem'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={appointment.status}
                      color={getStatusColor(appointment.status)}
                      size="small"
                      sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                    />
                  </TableCell>
                  <TableCell>
                    <ExpandableText text={appointment.notes} maxLength={50} />
                  </TableCell>
                  <TableCell>
                    <ExpandableText text={appointment.prescription_details} maxLength={50} />
                  </TableCell>
                  <TableCell>
                    {appointment.prescription_file ? (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EyeOutlined />}
                        onClick={() => window.open(appointment.prescription_file, '_blank')}
                        sx={{ textTransform: 'none' }}
                      >
                        View
                      </Button>
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {dayjs(appointment.created_at).format('MMM DD, YYYY hh:mm A')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Dialog>
  );
}

AppointmentDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.oneOfType([PropTypes.object, PropTypes.array])
};

export default AppointmentDetailsModal;
